import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { pageItems, type PageItem } from '../../lib/pagination';

const BOX = 'flex h-10 min-w-10 items-center justify-center rounded-xl px-3 text-sm font-semibold tabular-nums transition active:scale-95';
const IDLE =
  'border border-slate-200 bg-white text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 dark:focus-visible:ring-brand-400 dark:border-navy-700 dark:bg-navy-800 dark:text-slate-300 dark:hover:border-brand-500/50 dark:hover:text-brand-300';
const CURRENT = 'bg-brand-700 text-white shadow-sm dark:bg-brand-500';
const OFF = 'border border-slate-100 text-slate-300 dark:border-navy-800 dark:text-navy-600';

/**
 * Numbered page links. Each page is a real address, so it can be opened in a
 * new tab, shared, and reached by crawlers. Narrow screens show fewer numbers.
 */
export function Pagination({ page, pages, hrefFor }: { page: number; pages: number; hrefFor: (page: number) => string }) {
  if (pages <= 1) return null;

  const row = (items: PageItem[], className: string) => (
    <ul className={`items-center justify-center gap-1.5 ${className}`}>
      <li>
        {page > 1 ? (
          <Link to={hrefFor(page - 1)} aria-label="Trang trước" rel="prev" className={`${BOX} ${IDLE}`}>
            <ChevronLeft size={18} />
          </Link>
        ) : (
          <span aria-hidden className={`${BOX} ${OFF}`}><ChevronLeft size={18} /></span>
        )}
      </li>
      {items.map((it, i) =>
        it === 'gap' ? (
          <li key={`gap-${i}`} aria-hidden className="px-1 text-slate-500 dark:text-slate-400">…</li>
        ) : (
          <li key={it}>
            {it === page ? (
              <span aria-current="page" className={`${BOX} ${CURRENT}`}>{it}</span>
            ) : (
              <Link to={hrefFor(it)} aria-label={`Trang ${it}`} className={`${BOX} ${IDLE}`}>
                {it}
              </Link>
            )}
          </li>
        ),
      )}
      <li>
        {page < pages ? (
          <Link to={hrefFor(page + 1)} aria-label="Trang sau" rel="next" className={`${BOX} ${IDLE}`}>
            <ChevronRight size={18} />
          </Link>
        ) : (
          <span aria-hidden className={`${BOX} ${OFF}`}><ChevronRight size={18} /></span>
        )}
      </li>
    </ul>
  );

  return (
    <nav aria-label="Phân trang" className="mt-12">
      {row(pageItems(page, pages, 0), 'flex sm:hidden')}
      {row(pageItems(page, pages, 1), 'hidden sm:flex')}
    </nav>
  );
}
